<template>
  <div ref="containerRef">
    <!-- Render the custom crypto image if available and it's not failed -->
    <img
      v-if="isVisible && cryptoImage && !useFallback"
      :alt="`${symbol} icon`"
      :src="cryptoImage"
      :style="{ width: size, height: size }"
      @error="handleImageError"
      :key="`${symbol}-custom`"
    />

    <!-- Render the fallback SVG if there is no custom image and fallback is not active -->
    <img
      :alt="`${symbol} icon`"
      v-else-if="isVisible && !cryptoImage && !useFallback"
      :src="`/packages/crypto-currency-images/svg/${symbol}.svg`"
      :style="{ width: size, height: size }"
      @error="toggleFallback"
      :key="`${symbol}-svg`"
    />

    <!-- Show the fallback PNG if both custom image and SVG fail -->
    <img
      v-else-if="useFallback && isVisible"
      src="/packages/crypto-currency-images/png/fallback.png"
      :style="{ width: size, height: size }"
      :alt="`${symbol} fallback icon`"
      :key="`${symbol} fallback`"
    />
  </div>
</template>

<script setup>
import {
 ref, computed, watch, onMounted, onUnmounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useGlobalStore } from '@/stores/globalStore';

const containerRef = ref(null);
const isVisible = ref(false);
const useFallback = ref(false);
const cryptoImage = ref(null);
const globalStore = useGlobalStore();
const { coins } = storeToRefs(globalStore);

let observer = null;

const props = defineProps({
  symbol: { type: String, default: 'btc' },
  size: { type: String, default: '25px' },
});

const cryptoIcon = computed(() => coins.value.map((coin) => ({
    code: coin.coin_code,
    image_url: coin.image_url,
})));

const getCoinImage = (symbol) => {
  const coin = cryptoIcon.value.find((data) => data.code === symbol.toUpperCase());
  return coin ? coin.image_url : null;
};

const handleImageError = () => {
  useFallback.value = true;
};

const toggleFallback = () => {
  useFallback.value = true;
};

const setupIntersectionObserver = () => {
  observer = new IntersectionObserver((entries) => {
    const [entry] = entries;
    isVisible.value = entry.isIntersecting;
    if (entry.isIntersecting) {
      cryptoImage.value = getCoinImage(props.symbol);
      observer.disconnect();
    }
  });

  if (containerRef.value) {
    observer.observe(containerRef.value);
  }
};

onMounted(() => {
  setupIntersectionObserver();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

watch(
  () => props.symbol,
  () => {
    cryptoImage.value = getCoinImage(props.symbol);
    useFallback.value = false;
  },
  { immediate: true },
);
</script>
